import { useCallback } from "react";
import { message } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { isFunction } from "lodash";
import {
  CREATE_DEPARTMENT,
  UPDATE_DEPARTMENT,
  DELETE_DEPARTMENT,
} from "../graphql/mutation/mutation";
import { GET_DEPARTMENTS } from "../graphql/query";

const useDepartment = ({
  locationId,
  companyId,
  customSkipCondition,
  onCompleted,
  queryFetchPolicy,
} = {}) => {
  // Create department
  const [createDepartmentMutation, { loading: creating }] = useMutation(CREATE_DEPARTMENT, {
    onCompleted: () => message.success("Department created successfully"),
    onError: err => message.error(err?.message || "Failed to create department"),
  });

  const createDepartment = useCallback(
    async variables => {
      return createDepartmentMutation({ variables });
    },
    [createDepartmentMutation]
  );

  // Update department
  const [updateDepartmentMutation, { loading: updating }] = useMutation(UPDATE_DEPARTMENT, {
    onCompleted: () => message.success("Department updated successfully"),
    onError: err => message.error(err?.message || "Failed to update department"),
  });

  const updateDepartment = useCallback(
    async variables => {
      return updateDepartmentMutation({ variables });
    },
    [updateDepartmentMutation]
  );

  // Delete department
  const [deleteDepartmentMutation, { loading: deleting }] = useMutation(DELETE_DEPARTMENT, {
    onCompleted: response => {
      if (response?.deleteDepartment?.statusCode === 200) {
        message.success("Department deleted successfully");
      } else {
        message.error(response?.deleteDepartment?.message || "Failed to delete department");
      }
    },
    onError: err => message.error(err?.message || "Failed to delete department"),
  });

  const deleteDepartment = useCallback(
    async departmentId => {
      return deleteDepartmentMutation({ variables: { departmentId } });
    },
    [deleteDepartmentMutation]
  );

  // Determine variables and skip condition (require either locationId or companyId)
  const queryVariables = {
    ...(locationId ? { locationId } : {}),
    ...(companyId ? { companyId } : {}),
  };

  const shouldSkip =
    !(locationId || companyId) ||
    (isFunction(customSkipCondition) ? customSkipCondition() : customSkipCondition);

  // Fetch departments
  const {
    data,
    loading: fetching,
    error,
    refetch: refetchDepartments,
  } = useQuery(GET_DEPARTMENTS, {
    variables: queryVariables,
    skip: shouldSkip,
    fetchPolicy: queryFetchPolicy || "network-only",
    onCompleted: data => {
      if (onCompleted && data?.getDepartments) {
        onCompleted(data.getDepartments);
      }
    },
  });

  return {
    createDepartment,
    creating,
    updateDepartment,
    updating,
    deleteDepartment,
    deleting,
    departments: data?.getDepartments || [],
    fetching,
    error,
    refetchDepartments,
  };
};

export default useDepartment;
